import { useState, useEffect } from "react";
import { supabase } from "./supabase";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const STATUS_COLORS = {
  inquiry:   { bg: "#e8f0ff", text: "#3a58e8" },
  qualified: { bg: "#e0f5e9", text: "#2a7a4a" },
  booked:    { bg: "#fff3e0", text: "#d97706" },
  treated:   { bg: "#ede9fe", text: "#7c3aed" },
  cancelled: { bg: "#ffe8e8", text: "#e53e3e" },
  active:    { bg: "#e8f0ff", text: "#3a58e8" },
  inactive:  { bg: "#ffe8e8", text: "#e53e3e" },
  pending:   { bg: "#fff3e0", text: "#d97706" },
  paid:      { bg: "#e0f5e9", text: "#2a7a4a" },
};

const Badge = ({ status }) => {
  const c = STATUS_COLORS[status] || { bg: "#f0f3fa", text: "#6a7a9a" };
  return (
    <span style={{ background: c.bg, color: c.text, padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase" }}>
      {status}
    </span>
  );
};

const Icon = ({ name, size = 16 }) => {
  const icons = {
    dashboard: "M3 3h7v7H3zm11 0h7v7h-7zM3 14h7v7H3zm11 0h7v7h-7z",
    affiliates: "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm14 10v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75",
    leads: "M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 1 4 0M9 5h6m-3 4v6m-3-3h6",
    payments: "M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2zm1 14.93V18h-2v-1.07A4 4 0 0 1 8 13h2a2 2 0 0 0 4 0c0-1.1-.9-2-2-2a4 4 0 0 1 0-8V2h2v1.07A4 4 0 0 1 16 7h-2a2 2 0 0 0-4 0c0 1.1.9 2 2 2a4 4 0 0 1 0 8z",
    add: "M12 5v14m-7-7h14",
    link: "M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71",
    logout: "M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4m7 14 5-5-5-5m5 5H9",
    check: "M20 6L9 17l-5-5",
    eye: "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8zm11-3a3 3 0 1 0 0 6 3 3 0 0 0 0-6z",
    arrow: "M5 12h14m-7-7 7 7-7 7",
    copy: "M8 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-2M8 4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2V4zm4 6h6m-3-3v6",
    applications: "M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 1 4 0M9 5h6m-6 5h6m-6 4h4",
    x: "M18 6 6 18M6 6l12 12",
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d={icons[name]} />
    </svg>
  );
};

// ─── Main App ─────────────────────────────────────────────────────────────────
export default function App() {
  const [session, setSession] = useState(null);
  const [userRole, setUserRole] = useState(null); // "admin" | "affiliate"
  const [affiliateProfile, setAffiliateProfile] = useState(null);
  const [view, setView] = useState("dashboard");
  const [loaded, setLoaded] = useState(false);
  const [toast, setToast] = useState(null);

  const [affiliates, setAffiliates] = useState([]);
  const [leads, setLeads] = useState([]);
  const [clicks, setClicks] = useState([]);
  const [payments, setPayments] = useState([]);
  const [applications, setApplications] = useState([]);

  const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || "admin@incostra.com";

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Auth listener ────────────────────────────────────────────────────────────
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });
    return () => subscription.unsubscribe();
  }, []);

  // ── Load data when session available ────────────────────────────────────────
  useEffect(() => {
    if (!session) { setLoaded(true); return; }
    const email = session.user.email;
    const isAdmin = email === ADMIN_EMAIL;
    setUserRole(isAdmin ? "admin" : "affiliate");
    loadData(isAdmin, session.user.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const loadData = async (isAdmin, userId) => {
    try {
      if (isAdmin) {
        const [a, l, c, p, ap] = await Promise.all([
          supabase.from("affiliates").select("*").order("joined_at", { ascending: false }),
          supabase.from("leads").select("*").order("created_at", { ascending: false }),
          supabase.from("clicks").select("*"),
          supabase.from("payments").select("*").order("created_at", { ascending: false }),
          supabase.from("affiliate_applications").select("*").order("created_at", { ascending: false }),
        ]);
        setAffiliates(a.data || []);
        setLeads(l.data || []);
        setClicks(c.data || []);
        setPayments(p.data || []);
        setApplications(ap.data || []);
      } else {
        // Affiliate: load own profile then own data
        const { data: aff } = await supabase.from("affiliates").select("*").eq("auth_user_id", userId).single();
        if (aff) {
          setAffiliateProfile(aff);
          const [l, c, p] = await Promise.all([
            supabase.from("leads").select("*").eq("affiliate_id", aff.id).order("created_at", { ascending: false }),
            supabase.from("clicks").select("*").eq("affiliate_id", aff.id),
            supabase.from("payments").select("*").eq("affiliate_id", aff.id).order("created_at", { ascending: false }),
          ]);
          setLeads(l.data || []);
          setClicks(c.data || []);
          setPayments(p.data || []);
          setAffiliates([aff]);
        }
      }
    } catch (e) {
      console.error("Load error:", e);
      showToast("Error loading data", "error");
    }
    setLoaded(true);
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setUserRole(null);
    setAffiliateProfile(null);
  };

  // ── CRUD operations ──────────────────────────────────────────────────────────
  const addAffiliate = async (data) => {
    const { data: aff, error } = await supabase.from("affiliates").insert([data]).select().single();
    if (error) { showToast("Error creating affiliate", "error"); return; }
    setAffiliates(prev => [aff, ...prev]);
    showToast("Affiliate onboarded!");
  };

  const updateAffiliate = async (updated) => {
    const { error } = await supabase.from("affiliates").update(updated).eq("id", updated.id);
    if (error) { showToast("Update failed", "error"); return; }
    setAffiliates(prev => prev.map(a => a.id === updated.id ? updated : a));
    showToast("Affiliate updated");
  };

  const addLead = async (data) => {
    const { data: lead, error } = await supabase.from("leads").insert([data]).select().single();
    if (error) { showToast("Error adding lead", "error"); return; }
    setLeads(prev => [lead, ...prev]);
    // Record click
    const today = new Date().toISOString().slice(0, 10);
    const existing = clicks.find(c => c.affiliate_id === data.affiliate_id && c.date === today);
    if (existing) {
      await supabase.from("clicks").update({ count: existing.count + 1 }).eq("id", existing.id);
      setClicks(prev => prev.map(c => c.id === existing.id ? { ...c, count: c.count + 1 } : c));
    } else {
      const { data: click } = await supabase.from("clicks").insert([{ affiliate_id: data.affiliate_id, date: today, count: 1 }]).select().single();
      if (click) setClicks(prev => [...prev, click]);
    }
    showToast("Lead added!");
  };

  const updateLead = async (updated) => {
    const { error } = await supabase.from("leads").update(updated).eq("id", updated.id);
    if (error) { showToast("Update failed", "error"); return; }
    setLeads(prev => prev.map(l => l.id === updated.id ? updated : l));
    showToast("Lead updated");
  };

  const addPayment = async (data) => {
    const { data: pay, error } = await supabase.from("payments").insert([data]).select().single();
    if (error) { showToast("Error recording payment", "error"); return; }
    setPayments(prev => [pay, ...prev]);
    showToast("Payment recorded!");
  };

  const updatePayment = async (updated) => {
    const { error } = await supabase.from("payments").update(updated).eq("id", updated.id);
    if (error) { showToast("Update failed", "error"); return; }
    setPayments(prev => prev.map(p => p.id === updated.id ? updated : p));
    showToast("Payment updated");
  };

  if (!loaded) return (
    <div style={{ background: "#f0f3fa", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ color: "#5472ff", fontFamily: "DM Sans, sans-serif", fontSize: 14 }}>Loading...</div>
    </div>
  );

  if (!session) return <LoginScreen onLogin={() => {}} />;

  const isAdmin = userRole === "admin";

  return (
    <div style={{ background: "#f0f3fa", minHeight: "100vh", display: "flex", fontFamily: "'DM Sans', 'Segoe UI', sans-serif", color: "#1e2d4a" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Sora:wght@600;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        input, select, textarea { outline: none; font-family: inherit; }
        button { cursor: pointer; border: none; font-family: inherit; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: #c8d2f5; border-radius: 4px; }
        .nav-item:hover { background: #eef1fb !important; }
        .card-hover:hover { transform: translateY(-2px); box-shadow: 0 8px 32px rgba(84,114,255,0.12) !important; }
        .btn-primary:hover { opacity: 0.88; transform: translateY(-1px); }
        .row-hover:hover { background: #f4f6fb !important; }
      `}</style>

      {/* Sidebar */}
      <div style={{ width: 220, background: "#fff", borderRight: "1px solid #e2e8f0", display: "flex", flexDirection: "column", position: "sticky", top: 0, height: "100vh" }}>
        <div style={{ padding: "24px 20px 16px", borderBottom: "1px solid #e8ecf4" }}>
          <img src="/logo.png" alt="Incostra" style={{ width: 160, objectFit: "contain", display: "block", margin: "0 auto 4px" }} />
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", color: "#8899bb", textAlign: "center", marginTop: 2 }}>AFFILIATE PORTAL</div>
        </div>
        <div style={{ padding: "8px 10px", flex: 1 }}>
          {[
            { key: "dashboard", label: "Dashboard", icon: "dashboard" },
            ...(isAdmin ? [
              { key: "affiliates", label: "Affiliates", icon: "affiliates" },
              { key: "applications", label: "Applications", icon: "applications", badge: applications.filter(a => a.status === "pending").length },
            ] : [{ key: "mylink", label: "My Link", icon: "link" }]),
            { key: "leads", label: "Leads", icon: "leads" },
            { key: "payments", label: "Payments", icon: "payments" },
          ].map(item => (
            <button key={item.key} className="nav-item" onClick={() => setView(item.key)}
              style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 8, marginBottom: 2, background: view === item.key ? "#eef1fb" : "transparent", color: view === item.key ? "#5472ff" : "#8899bb", fontSize: 13, fontWeight: 500, textAlign: "left", border: view === item.key ? "1px solid #c8d2f5" : "1px solid transparent", transition: "all 0.15s" }}>
              <Icon name={item.icon} size={15} />
              <span style={{ flex: 1 }}>{item.label}</span>
              {item.badge > 0 && <span style={{ background: "#e53e3e", color: "#fff", borderRadius: 10, fontSize: 10, fontWeight: 700, padding: "1px 6px", minWidth: 18, textAlign: "center" }}>{item.badge}</span>}
            </button>
          ))}
        </div>
        <div style={{ padding: "12px 10px", borderTop: "1px solid #e8ecf4" }}>
          <div style={{ padding: "8px 12px", marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: "#8899bb", marginBottom: 2 }}>Logged in as</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#1e2d4a" }}>{isAdmin ? "Admin" : affiliateProfile?.name}</div>
            {!isAdmin && <div style={{ fontSize: 11, color: "#5472ff" }}>{affiliateProfile?.niche}</div>}
          </div>
          <button className="nav-item" onClick={logout}
            style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 8, background: "transparent", color: "#8899bb", fontSize: 13, fontWeight: 500, border: "1px solid transparent" }}>
            <Icon name="logout" size={15} /> Sign Out
          </button>
        </div>
      </div>

      {/* Main */}
      <div style={{ flex: 1, overflowY: "auto", padding: "32px" }}>
        {view === "dashboard" && <Dashboard affiliates={affiliates} leads={leads} clicks={clicks} payments={payments} applications={applications} isAdmin={isAdmin} affiliateUser={affiliateProfile} onViewApplications={() => setView("applications")} />}
        {view === "affiliates" && isAdmin && <AffiliatesView affiliates={affiliates} leads={leads} clicks={clicks} payments={payments} onAdd={addAffiliate} onUpdate={updateAffiliate} showToast={showToast} />}
        {view === "mylink" && !isAdmin && <MyLink affiliate={affiliateProfile} clicks={clicks} />}
        {view === "leads" && <LeadsView leads={leads} affiliates={affiliates} isAdmin={isAdmin} onAdd={addLead} onUpdate={updateLead} showToast={showToast} />}
        {view === "payments" && <PaymentsView payments={payments} leads={leads} affiliates={affiliates} isAdmin={isAdmin} onAdd={addPayment} onUpdate={updatePayment} showToast={showToast} />}
        {view === "applications" && isAdmin && <ApplicationsView applications={applications} onUpdate={(updated) => setApplications(prev => prev.map(a => a.id === updated.id ? updated : a))} onApprove={(app) => { const code = app.name.split(" ")[0].toUpperCase() + Math.floor(Math.random() * 20 + 10); const newAff = { name: app.name, email: app.email, phone: app.phone, country: app.country, niche: app.niche, website: app.website, code, commission_rate: 20, status: "active", joined_at: new Date().toISOString().slice(0,10), notes: "" }; addAffiliate(newAff); supabase.from("affiliate_applications").update({ status: "approved" }).eq("id", app.id).then(() => setApplications(prev => prev.map(a => a.id === app.id ? { ...a, status: "approved" } : a))); showToast(`${app.name} approved and onboarded!`); }} onReject={(app) => { supabase.from("affiliate_applications").update({ status: "rejected" }).eq("id", app.id).then(() => setApplications(prev => prev.map(a => a.id === app.id ? { ...a, status: "rejected" } : a))); showToast("Application rejected"); }} showToast={showToast} />}
      </div>

      {toast && (
        <div style={{ position: "fixed", bottom: 24, right: 24, background: toast.type === "success" ? "#f0f4ff" : "#fff0f0", border: `1px solid ${toast.type === "success" ? "#c0ccf5" : "#f5c0c0"}`, color: toast.type === "success" ? "#3a58e8" : "#e53e3e", padding: "12px 20px", borderRadius: 10, fontSize: 13, fontWeight: 500, boxShadow: "0 8px 32px rgba(0,0,0,0.1)", zIndex: 9999 }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}

// ─── Login Screen ─────────────────────────────────────────────────────────────
function LoginScreen() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("login");
  const [reg, setReg] = useState({ name: "", email: "", phone: "", country: "", niche: "", website: "" });
  const [regSuccess, setRegSuccess] = useState(false);

  const handleLogin = async () => {
    setError(""); setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) setError("Invalid email or password.");
    setLoading(false);
  };

  const handleRegister = async () => {
    if (!reg.name || !reg.email || !reg.niche || !reg.country) { setError("Please fill all required fields"); return; }
    setError(""); setLoading(true);
    // Just store the application — admin will create auth account
    const code = reg.name.split(" ")[0].toUpperCase() + Math.floor(Math.random() * 20 + 10);
    const { error } = await supabase.from("affiliate_applications").insert([{ ...reg, code, status: "pending" }]);
    if (error) { setError("Submission failed. Please try again."); setLoading(false); return; }
    setRegSuccess(true);
    setLoading(false);
  };

  const inp = { width: "100%", background: "#f4f6fb", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px", color: "#1e2d4a", fontSize: 13, marginBottom: 12 };

  return (
    <div style={{ background: "#f0f3fa", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap'); * {box-sizing:border-box;margin:0;padding:0;} input,select{outline:none;font-family:inherit;}`}</style>
      <div style={{ width: 400 }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <img src="/logo.png" alt="Incostra" style={{ width: 200, objectFit: "contain", display: "block", margin: "0 auto 12px" }} />
          <div style={{ fontSize: 20, fontWeight: 700, color: "#1e2d4a", fontFamily: "DM Sans, sans-serif" }}>Affiliate Portal</div>
        </div>
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 16, padding: 28, boxShadow: "0 4px 24px rgba(84,114,255,0.08)" }}>
          {regSuccess ? (
            <div style={{ textAlign: "center", padding: "16px 0" }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>✓</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#1e2d4a", marginBottom: 8 }}>Application received</div>
              <div style={{ fontSize: 13, color: "#6a7a9a", lineHeight: 1.6 }}>Our team will review your application and send your login details by email within 48 hours.</div>
            </div>
          ) : (
            <>
              <div style={{ display: "flex", marginBottom: 20, background: "#f0f3fa", borderRadius: 8, padding: 4 }}>
                {[["login", "Sign In"], ["register", "Apply as Affiliate"]].map(([m, l]) => (
                  <button key={m} onClick={() => { setMode(m); setError(""); }}
                    style={{ flex: 1, padding: "8px", borderRadius: 6, background: mode === m ? "#fff" : "transparent", color: mode === m ? "#5472ff" : "#8899bb", fontSize: 12, fontWeight: 600, border: "none", transition: "all 0.15s", boxShadow: mode === m ? "0 1px 4px rgba(0,0,0,0.08)" : "none" }}>
                    {l}
                  </button>
                ))}
              </div>

              {mode === "login" ? (
                <>
                  <input style={inp} placeholder="Email address" value={email} onChange={e => setEmail(e.target.value)} />
                  <input style={inp} type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && handleLogin()} />
                  {error && <div style={{ color: "#e53e3e", fontSize: 12, marginBottom: 12 }}>{error}</div>}
                  <button onClick={handleLogin} disabled={loading}
                    style={{ width: "100%", background: "#5472ff", border: "none", borderRadius: 8, padding: "12px", color: "#fff", fontSize: 13, fontWeight: 600, opacity: loading ? 0.7 : 1 }}>
                    {loading ? "Signing in..." : "Sign In"}
                  </button>
                </>
              ) : (
                <>
                  {[["name","Full Name *"],["email","Email *"],["phone","Phone"],["website","Website / Social Handle"]].map(([f,p]) => (
                    <input key={f} style={inp} placeholder={p} value={reg[f]} onChange={e => setReg({ ...reg, [f]: e.target.value })} />
                  ))}
                  <select style={{ ...inp, cursor: "pointer" }} value={reg.country} onChange={e => setReg({ ...reg, country: e.target.value })}>
                    <option value="">Country *</option>
                    {["UAE","Saudi Arabia","Kuwait","Qatar","Russia","Ukraine","Kazakhstan","UK","Germany","France","Netherlands","Other"].map(c => <option key={c}>{c}</option>)}
                  </select>
                  <select style={{ ...inp, cursor: "pointer" }} value={reg.niche} onChange={e => setReg({ ...reg, niche: e.target.value })}>
                    <option value="">Your niche *</option>
                    {["MUA / Aesthetician","Personal Trainer","Nutritionist","Wellness Blogger","Travel Agent","Healthcare Professional","Influencer","Other"].map(n => <option key={n}>{n}</option>)}
                  </select>
                  {error && <div style={{ color: "#e53e3e", fontSize: 12, marginBottom: 12 }}>{error}</div>}
                  <button onClick={handleRegister} disabled={loading}
                    style={{ width: "100%", background: "#5472ff", border: "none", borderRadius: 8, padding: "12px", color: "#fff", fontSize: 13, fontWeight: 600, opacity: loading ? 0.7 : 1 }}>
                    {loading ? "Submitting..." : "Submit Application"}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────
function Dashboard({ affiliates, leads, clicks, payments, applications, isAdmin, affiliateUser, onViewApplications }) {
  const totalClicks = clicks.reduce((s, c) => s + (c.count || 0), 0);
  const treatedLeads = leads.filter(l => l.status === "treated");
  const totalBilling = treatedLeads.reduce((s, l) => s + (l.billing_amount || 0), 0);
  const totalCommission = treatedLeads.reduce((s, l) => s + (l.commission_amount || 0), 0);
  const paidOut = payments.filter(p => p.status === "paid").reduce((s, p) => s + (p.amount || 0), 0);
  const conversionRate = totalClicks > 0 ? ((leads.length / totalClicks) * 100).toFixed(1) : 0;

  const stats = isAdmin ? [
    { label: "Total Affiliates", value: affiliates.length, sub: `${affiliates.filter(a => a.status === "active").length} active`, color: "#5472ff" },
    { label: "Total Clicks", value: totalClicks, sub: `${conversionRate}% conversion`, color: "#7c3aed" },
    { label: "Total Leads", value: leads.length, sub: `${treatedLeads.length} treated`, color: "#d97706" },
    { label: "Total Billing", value: fmt(totalBilling), sub: "Treated patients", color: "#2a7a4a" },
    { label: "Commission Earned", value: fmt(totalCommission), sub: "On treated patients", color: "#d97706" },
    { label: "Pending Payout", value: fmt(totalCommission - paidOut), sub: `${fmt(paidOut)} paid out`, color: "#e53e3e" },
  ] : [
    { label: "My Clicks", value: totalClicks, sub: `${conversionRate}% lead conversion`, color: "#5472ff" },
    { label: "My Leads", value: leads.length, sub: `${treatedLeads.length} treated`, color: "#d97706" },
    { label: "Commission Earned", value: fmt(totalCommission), sub: "On treated patients", color: "#2a7a4a" },
    { label: "Pending Payout", value: fmt(totalCommission - paidOut), sub: `${fmt(paidOut)} paid`, color: "#d97706" },
  ];

  const recentLeads = [...leads].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 5);

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 22, fontWeight: 700, fontFamily: "'Sora', sans-serif", color: "#1e2d4a" }}>
          {isAdmin ? "Admin Dashboard" : `Welcome back, ${affiliateUser?.name?.split(" ")[0]}`}
        </div>
        <div style={{ fontSize: 13, color: "#6a7a9a", marginTop: 4 }}>Overview of affiliate activity</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${isAdmin ? 3 : 2}, 1fr)`, gap: 14, marginBottom: 28 }}>
        {stats.map((s, i) => (
          <div key={i} className="card-hover" style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "18px 20px", transition: "all 0.2s" }}>
            <div style={{ fontSize: 11, color: "#8899bb", fontWeight: 600, letterSpacing: "0.05em", marginBottom: 6, textTransform: "uppercase" }}>{s.label}</div>
            <div style={{ fontSize: 26, fontWeight: 700, fontFamily: "'Sora', sans-serif", color: s.color, marginBottom: 4 }}>{s.value}</div>
            <div style={{ fontSize: 11, color: "#8899bb" }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {isAdmin && applications.filter(a => a.status === "pending").length > 0 && (
        <div style={{ background: "#fff5e0", border: "1px solid #ffd080", borderRadius: 12, padding: 20, marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 40, height: 40, borderRadius: 20, background: "#fff3cd", border: "1px solid #ffd080", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>📋</div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#92400e" }}>
                {applications.filter(a => a.status === "pending").length} pending affiliate application{applications.filter(a => a.status === "pending").length > 1 ? "s" : ""}
              </div>
              <div style={{ fontSize: 12, color: "#b45309", marginTop: 2 }}>Review and approve new affiliate applications</div>
            </div>
          </div>
          <button onClick={onViewApplications}
            style={{ background: "#d97706", border: "none", borderRadius: 8, padding: "9px 18px", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            Review Now →
          </button>
        </div>
      )}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20, marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#1e2d4a", marginBottom: 16 }}>Lead Pipeline</div>
        <div style={{ display: "flex", gap: 8 }}>
          {["inquiry","qualified","booked","treated","cancelled"].map(s => {
            const count = leads.filter(l => l.status === s).length;
            const c = STATUS_COLORS[s];
            return (
              <div key={s} style={{ flex: 1, background: c.bg, borderRadius: 8, padding: "12px 10px", textAlign: "center" }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: c.text, fontFamily: "'Sora', sans-serif" }}>{count}</div>
                <div style={{ fontSize: 10, color: c.text, opacity: 0.8, textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 2 }}>{s}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#1e2d4a", marginBottom: 16 }}>Recent Leads</div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>{["Patient","Procedure","Status","Billing","Date"].map(h => (
              <th key={h} style={{ textAlign: "left", fontSize: 11, color: "#8899bb", fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", paddingBottom: 10, borderBottom: "1px solid #e2e8f0" }}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {recentLeads.map(l => (
              <tr key={l.id} className="row-hover" style={{ transition: "background 0.1s" }}>
                <td style={{ padding: "10px 0", fontSize: 13, color: "#1e2d4a" }}>{l.name}</td>
                <td style={{ padding: "10px 0", fontSize: 13, color: "#6a7a9a" }}>{l.procedure}</td>
                <td style={{ padding: "10px 0" }}><Badge status={l.status} /></td>
                <td style={{ padding: "10px 0", fontSize: 13, color: "#2a7a4a" }}>{l.billing_amount ? fmt(l.billing_amount) : "—"}</td>
                <td style={{ padding: "10px 0", fontSize: 12, color: "#8899bb" }}>{fmtDate(l.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Affiliates View ──────────────────────────────────────────────────────────
function AffiliatesView({ affiliates, leads, clicks, payments, onAdd, onUpdate, showToast }) {
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", country: "", niche: "", website: "", commission_rate: 20, notes: "" });

  const getStats = (id) => ({
    clicks: clicks.filter(c => c.affiliate_id === id).reduce((s, c) => s + (c.count || 0), 0),
    leads: leads.filter(l => l.affiliate_id === id).length,
    commission: leads.filter(l => l.affiliate_id === id && l.status === "treated").reduce((s, l) => s + (l.commission_amount || 0), 0),
    paid: payments.filter(p => p.affiliate_id === id && p.status === "paid").reduce((s, p) => s + (p.amount || 0), 0),
  });

  const submit = () => {
    if (!form.name || !form.email) { showToast("Name and email required", "error"); return; }
    const code = form.name.split(" ")[0].toUpperCase() + Math.floor(Math.random() * 20 + 10);
    onAdd({ ...form, code, status: "active", joined_at: new Date().toISOString().slice(0, 10) });
    setForm({ name: "", email: "", phone: "", country: "", niche: "", website: "", commission_rate: 20, notes: "" });
    setShowForm(false);
  };

  const inp = { width: "100%", background: "#f4f6fb", border: "1px solid #e2e8f0", borderRadius: 8, padding: "9px 12px", color: "#1e2d4a", fontSize: 13, marginBottom: 10 };

  if (selected) {
    const s = getStats(selected.id);
    const affLeads = leads.filter(l => l.affiliate_id === selected.id);
    return (
      <div>
        <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", color: "#8899bb", fontSize: 13, marginBottom: 20, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>← Back</button>
        <div style={{ display: "flex", gap: 20 }}>
          <div style={{ flex: 1, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 20, fontWeight: 700, color: "#1e2d4a" }}>{selected.name}</div>
                <div style={{ fontSize: 13, color: "#6a7a9a", marginTop: 4 }}>{selected.niche} · {selected.country}</div>
              </div>
              <Badge status={selected.status} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              {[["Email",selected.email],["Phone",selected.phone],["Website",selected.website],["Joined",fmtDate(selected.joined_at)],["Code",selected.code],["Commission",`${selected.commission_rate}%`]].map(([k,v]) => (
                <div key={k}>
                  <div style={{ fontSize: 11, color: "#8899bb", marginBottom: 2, textTransform: "uppercase", letterSpacing: "0.05em" }}>{k}</div>
                  <div style={{ fontSize: 13, color: "#1e2d4a", fontWeight: 500 }}>{v || "—"}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 16, padding: "10px 14px", background: "#f4f6fb", borderRadius: 8, fontSize: 12, color: "#5472ff", fontFamily: "monospace", border: "1px solid #e2e8f0" }}>
              https://go.incostra.com?ref={selected.code}
            </div>
          </div>
          <div style={{ width: 200 }}>
            {[["Clicks",s.clicks,"#5472ff"],["Leads",s.leads,"#d97706"],["Earned",fmt(s.commission),"#2a7a4a"],["Paid",fmt(s.paid),"#d97706"],["Pending",fmt(s.commission - s.paid),"#e53e3e"]].map(([l,v,c]) => (
              <div key={l} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: "14px 16px", marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: "#8899bb", marginBottom: 4, textTransform: "uppercase" }}>{l}</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: c, fontFamily: "'Sora', sans-serif" }}>{v}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20, marginTop: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#1e2d4a", marginBottom: 14 }}>Leads from {selected.name}</div>
          {affLeads.length === 0 ? <div style={{ color: "#8899bb", fontSize: 13 }}>No leads yet</div> : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>{["Patient","Procedure","Status","Billing","Commission","Date"].map(h => <th key={h} style={{ textAlign: "left", fontSize: 11, color: "#8899bb", paddingBottom: 8, borderBottom: "1px solid #e2e8f0", textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</th>)}</tr></thead>
              <tbody>
                {affLeads.map(l => (
                  <tr key={l.id} className="row-hover" style={{ borderBottom: "1px solid #f0f3fa" }}>
                    <td style={{ padding: "9px 0", fontSize: 13, color: "#1e2d4a" }}>{l.name}</td>
                    <td style={{ padding: "9px 0", fontSize: 12, color: "#6a7a9a" }}>{l.procedure}</td>
                    <td style={{ padding: "9px 0" }}><Badge status={l.status} /></td>
                    <td style={{ padding: "9px 0", fontSize: 13 }}>{l.billing_amount ? fmt(l.billing_amount) : "—"}</td>
                    <td style={{ padding: "9px 0", fontSize: 13, color: "#2a7a4a" }}>{l.commission_amount ? fmt(l.commission_amount) : "—"}</td>
                    <td style={{ padding: "9px 0", fontSize: 12, color: "#8899bb" }}>{fmtDate(l.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, fontFamily: "'Sora', sans-serif", color: "#1e2d4a" }}>Affiliates</div>
          <div style={{ fontSize: 13, color: "#6a7a9a", marginTop: 4 }}>{affiliates.length} partners registered</div>
        </div>
        <button className="btn-primary" onClick={() => setShowForm(true)}
          style={{ display: "flex", alignItems: "center", gap: 8, background: "#5472ff", border: "none", borderRadius: 8, padding: "10px 16px", color: "#fff", fontSize: 13, fontWeight: 600, transition: "all 0.15s" }}>
          <Icon name="add" size={14} /> Onboard Affiliate
        </button>
      </div>

      {showForm && (
        <div style={{ background: "#fff", border: "1px solid #c8d2f5", borderRadius: 12, padding: 24, marginBottom: 20 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#1e2d4a", marginBottom: 16 }}>Onboard New Affiliate</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {[["name","Full Name *"],["email","Email *"],["phone","Phone"],["country","Country"],["website","Website / Social Handle"],["niche","Niche"]].map(([f,p]) => (
              <input key={f} style={inp} placeholder={p} value={form[f]} onChange={e => setForm({ ...form, [f]: e.target.value })} />
            ))}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
            <label style={{ fontSize: 12, color: "#6a7a9a" }}>Commission Rate (%)</label>
            <input type="number" style={{ ...inp, width: 80, marginBottom: 0 }} value={form.commission_rate} onChange={e => setForm({ ...form, commission_rate: +e.target.value })} />
          </div>
          <textarea style={{ ...inp, resize: "none", height: 64 }} placeholder="Notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
          <div style={{ display: "flex", gap: 10 }}>
            <button className="btn-primary" onClick={submit} style={{ background: "#5472ff", border: "none", borderRadius: 8, padding: "10px 20px", color: "#fff", fontSize: 13, fontWeight: 600, transition: "all 0.15s" }}>Create Affiliate</button>
            <button onClick={() => setShowForm(false)} style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 16px", color: "#8899bb", fontSize: 13 }}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ display: "grid", gap: 12 }}>
        {affiliates.map(aff => {
          const s = getStats(aff.id);
          return (
            <div key={aff.id} className="card-hover" onClick={() => setSelected(aff)}
              style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "16px 20px", cursor: "pointer", display: "flex", alignItems: "center", gap: 20, transition: "all 0.2s" }}>
              <div style={{ width: 44, height: 44, borderRadius: 22, background: "#eef1fb", border: "1px solid #c8d2f5", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 700, color: "#5472ff", flexShrink: 0 }}>
                {aff.name?.charAt(0)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 3 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: "#1e2d4a" }}>{aff.name}</span>
                  <Badge status={aff.status} />
                </div>
                <div style={{ fontSize: 12, color: "#8899bb" }}>{aff.niche} · {aff.country} · <span style={{ color: "#5472ff" }}>{aff.code}</span></div>
              </div>
              <div style={{ display: "flex", gap: 24, textAlign: "center" }}>
                {[["Clicks",s.clicks,"#7c3aed"],["Leads",s.leads,"#d97706"],["Earned",fmt(s.commission),"#2a7a4a"],["Pending",fmt(s.commission-s.paid),"#e53e3e"]].map(([l,v,c]) => (
                  <div key={l}><div style={{ fontSize: 15, fontWeight: 700, color: c }}>{v}</div><div style={{ fontSize: 10, color: "#8899bb", textTransform: "uppercase" }}>{l}</div></div>
                ))}
              </div>
              <div style={{ color: "#8899bb" }}><Icon name="arrow" size={16} /></div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── My Link ──────────────────────────────────────────────────────────────────
function MyLink({ affiliate, clicks }) {
  const [copied, setCopied] = useState(false);
  const link = `https://go.incostra.com?ref=${affiliate?.code}`;
  const totalClicks = clicks.reduce((s, c) => s + (c.count || 0), 0);

  const copy = () => {
    navigator.clipboard.writeText(link).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div>
      <div style={{ fontSize: 22, fontWeight: 700, fontFamily: "'Sora', sans-serif", color: "#1e2d4a", marginBottom: 6 }}>My Referral Link</div>
      <div style={{ fontSize: 13, color: "#6a7a9a", marginBottom: 28 }}>Share this link to refer patients to Incostra</div>
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: 28, marginBottom: 20 }}>
        <div style={{ fontSize: 12, color: "#8899bb", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Your Unique Link</div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <div style={{ flex: 1, background: "#f4f6fb", border: "1px solid #c8d2f5", borderRadius: 10, padding: "14px 18px", fontSize: 14, color: "#5472ff", fontFamily: "monospace" }}>{link}</div>
          <button className="btn-primary" onClick={copy}
            style={{ display: "flex", alignItems: "center", gap: 8, background: copied ? "#e0f5e9" : "#5472ff", border: "none", borderRadius: 10, padding: "14px 20px", color: copied ? "#2a7a4a" : "#fff", fontSize: 13, fontWeight: 600, transition: "all 0.15s" }}>
            {copied ? <><Icon name="check" size={14} /> Copied!</> : <><Icon name="copy" size={14} /> Copy</>}
          </button>
        </div>
        <div style={{ marginTop: 14, padding: "10px 14px", background: "#f4f6fb", borderRadius: 8 }}>
          <span style={{ fontSize: 12, color: "#8899bb" }}>Code: </span>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#1e2d4a", fontFamily: "monospace" }}>{affiliate?.code}</span>
          <span style={{ fontSize: 12, color: "#8899bb", marginLeft: 16 }}>Commission: </span>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#2a7a4a" }}>{affiliate?.commission_rate}%</span>
        </div>
      </div>
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "14px 20px" }}>
        <div style={{ fontSize: 11, color: "#8899bb", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Total Link Clicks</div>
        <div style={{ fontSize: 36, fontWeight: 700, color: "#5472ff", fontFamily: "'Sora', sans-serif" }}>{totalClicks}</div>
      </div>
    </div>
  );
}

// ─── Leads View ───────────────────────────────────────────────────────────────
function LeadsView({ leads, affiliates, isAdmin, onAdd, onUpdate, showToast }) {
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState({ name: "", email: "", country: "", phone: "", procedure: "", affiliate_id: "", billing_amount: "", notes: "" });
  const STATUSES = ["inquiry","qualified","booked","treated","cancelled"];
  const PROCEDURES = ["Hair Transplant","Hair Restoration","Anti-Ageing","Aesthetics","Wellness Package","Aesthetic Surgery","Dental","Other"];

  const filtered = filter === "all" ? leads : leads.filter(l => l.status === filter);

  const submit = () => {
    if (!form.name || !form.affiliate_id) { showToast("Patient name and affiliate required", "error"); return; }
    const aff = affiliates.find(a => a.id === form.affiliate_id);
    const billing = parseFloat(form.billing_amount) || 0;
    const commission = billing * (aff?.commission_rate || 20) / 100;
    onAdd({ ...form, billing_amount: billing, commission_amount: commission, status: "inquiry" });
    setForm({ name: "", email: "", country: "", phone: "", procedure: "", affiliate_id: "", billing_amount: "", notes: "" });
    setShowForm(false);
  };

  const inp = { background: "#f4f6fb", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 12px", color: "#1e2d4a", fontSize: 13 };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, fontFamily: "'Sora', sans-serif", color: "#1e2d4a" }}>Leads</div>
          <div style={{ fontSize: 13, color: "#6a7a9a", marginTop: 4 }}>{leads.length} total leads</div>
        </div>
        {isAdmin && (
          <button className="btn-primary" onClick={() => setShowForm(true)}
            style={{ display: "flex", alignItems: "center", gap: 8, background: "#5472ff", border: "none", borderRadius: 8, padding: "10px 16px", color: "#fff", fontSize: 13, fontWeight: 600, transition: "all 0.15s" }}>
            <Icon name="add" size={14} /> Add Lead
          </button>
        )}
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
        {["all", ...STATUSES].map(s => (
          <button key={s} onClick={() => setFilter(s)}
            style={{ padding: "5px 12px", borderRadius: 20, fontSize: 11, fontWeight: 600, border: `1px solid ${filter === s ? "#c8d2f5" : "#e2e8f0"}`, background: filter === s ? "#eef1fb" : "transparent", color: filter === s ? "#5472ff" : "#8899bb", cursor: "pointer", textTransform: "capitalize" }}>
            {s === "all" ? `All (${leads.length})` : `${s} (${leads.filter(l => l.status === s).length})`}
          </button>
        ))}
      </div>

      {showForm && isAdmin && (
        <div style={{ background: "#fff", border: "1px solid #c8d2f5", borderRadius: 12, padding: 24, marginBottom: 20 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#1e2d4a", marginBottom: 16 }}>Add New Lead</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {[["name","Patient Name *"],["email","Email"],["country","Country"],["phone","Phone"]].map(([f,p]) => (
              <input key={f} style={{ ...inp, width: "100%" }} placeholder={p} value={form[f]} onChange={e => setForm({ ...form, [f]: e.target.value })} />
            ))}
            <select style={{ ...inp, width: "100%", cursor: "pointer" }} value={form.affiliate_id} onChange={e => setForm({ ...form, affiliate_id: e.target.value })}>
              <option value="">Select Affiliate *</option>
              {affiliates.map(a => <option key={a.id} value={a.id}>{a.name} ({a.code})</option>)}
            </select>
            <select style={{ ...inp, width: "100%", cursor: "pointer" }} value={form.procedure} onChange={e => setForm({ ...form, procedure: e.target.value })}>
              <option value="">Procedure</option>
              {PROCEDURES.map(p => <option key={p}>{p}</option>)}
            </select>
            <input style={{ ...inp, width: "100%" }} placeholder="Billing Amount (USD)" type="number" value={form.billing_amount} onChange={e => setForm({ ...form, billing_amount: e.target.value })} />
          </div>
          <textarea style={{ ...inp, width: "100%", resize: "none", height: 60, marginTop: 12 }} placeholder="Notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
          <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
            <button className="btn-primary" onClick={submit} style={{ background: "#5472ff", border: "none", borderRadius: 8, padding: "10px 20px", color: "#fff", fontSize: 13, fontWeight: 600, transition: "all 0.15s" }}>Save Lead</button>
            <button onClick={() => setShowForm(false)} style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 16px", color: "#8899bb", fontSize: 13 }}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #e2e8f0" }}>
              {["Patient","Country","Procedure","Affiliate","Status","Billing","Commission","Date"].map(h => (
                <th key={h} style={{ textAlign: "left", fontSize: 10, color: "#8899bb", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", padding: "12px 14px" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={8} style={{ padding: 40, textAlign: "center", color: "#8899bb", fontSize: 13 }}>No leads found</td></tr>}
            {filtered.map(lead => {
              const aff = affiliates.find(a => a.id === lead.affiliate_id);
              return (
                <tr key={lead.id} className="row-hover" style={{ borderBottom: "1px solid #f0f3fa", transition: "background 0.1s" }}>
                  <td style={{ padding: "11px 14px" }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: "#1e2d4a" }}>{lead.name}</div>
                    <div style={{ fontSize: 11, color: "#8899bb" }}>{lead.email}</div>
                  </td>
                  <td style={{ padding: "11px 14px", fontSize: 12, color: "#6a7a9a" }}>{lead.country || "—"}</td>
                  <td style={{ padding: "11px 14px", fontSize: 12, color: "#6a7a9a" }}>{lead.procedure || "—"}</td>
                  <td style={{ padding: "11px 14px", fontSize: 12, color: "#5472ff" }}>{aff?.name || "—"}</td>
                  <td style={{ padding: "11px 14px" }}>
                    {isAdmin ? (
                      <select value={lead.status} onChange={e => onUpdate({ ...lead, status: e.target.value })}
                        style={{ background: "#f4f6fb", border: "1px solid #e2e8f0", borderRadius: 6, padding: "4px 8px", color: "#1e2d4a", fontSize: 11, cursor: "pointer" }}>
                        {STATUSES.map(s => <option key={s}>{s}</option>)}
                      </select>
                    ) : <Badge status={lead.status} />}
                  </td>
                  <td style={{ padding: "11px 14px", fontSize: 13 }}>{lead.billing_amount ? fmt(lead.billing_amount) : "—"}</td>
                  <td style={{ padding: "11px 14px", fontSize: 13, color: "#2a7a4a" }}>{lead.commission_amount ? fmt(lead.commission_amount) : "—"}</td>
                  <td style={{ padding: "11px 14px", fontSize: 11, color: "#8899bb" }}>{fmtDate(lead.created_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Payments View ────────────────────────────────────────────────────────────
function PaymentsView({ payments, leads, affiliates, isAdmin, onAdd, onUpdate, showToast }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ affiliate_id: "", amount: "", reference: "", notes: "" });

  const totalPaid = payments.filter(p => p.status === "paid").reduce((s, p) => s + (p.amount || 0), 0);

  const pendingByAffiliate = affiliates.map(aff => {
    const earned = leads.filter(l => l.affiliate_id === aff.id && l.status === "treated").reduce((s, l) => s + (l.commission_amount || 0), 0);
    const paid = payments.filter(p => p.affiliate_id === aff.id && p.status === "paid").reduce((s, p) => s + (p.amount || 0), 0);
    return { ...aff, earned, paid, pending: earned - paid };
  }).filter(a => a.earned > 0);

  const totalOutstanding = pendingByAffiliate.reduce((s, a) => s + a.pending, 0);

  const submit = () => {
    if (!form.affiliate_id || !form.amount) { showToast("Affiliate and amount required", "error"); return; }
    onAdd({ ...form, amount: parseFloat(form.amount), status: "pending" });
    setForm({ affiliate_id: "", amount: "", reference: "", notes: "" });
    setShowForm(false);
  };

  const inp = { background: "#f4f6fb", border: "1px solid #e2e8f0", borderRadius: 8, padding: "9px 12px", color: "#1e2d4a", fontSize: 13, width: "100%" };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, fontFamily: "'Sora', sans-serif", color: "#1e2d4a" }}>Payments</div>
          <div style={{ fontSize: 13, color: "#6a7a9a", marginTop: 4 }}>Commission tracking — manual bank transfers</div>
        </div>
        {isAdmin && (
          <button className="btn-primary" onClick={() => setShowForm(true)}
            style={{ display: "flex", alignItems: "center", gap: 8, background: "#5472ff", border: "none", borderRadius: 8, padding: "10px 16px", color: "#fff", fontSize: 13, fontWeight: 600, transition: "all 0.15s" }}>
            <Icon name="add" size={14} /> Record Payment
          </button>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 24 }}>
        {[["Total Paid Out",fmt(totalPaid),"#2a7a4a"],["Outstanding",fmt(totalOutstanding),"#d97706"],["Records",payments.length,"#5472ff"]].map(([l,v,c]) => (
          <div key={l} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "18px 20px" }}>
            <div style={{ fontSize: 11, color: "#8899bb", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>{l}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: c, fontFamily: "'Sora', sans-serif" }}>{v}</div>
          </div>
        ))}
      </div>

      {isAdmin && pendingByAffiliate.length > 0 && (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20, marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#1e2d4a", marginBottom: 14 }}>Outstanding Commissions</div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>{["Affiliate","Niche","Earned","Paid","Outstanding"].map(h => <th key={h} style={{ textAlign: "left", fontSize: 10, color: "#8899bb", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", paddingBottom: 10, borderBottom: "1px solid #e2e8f0" }}>{h}</th>)}</tr></thead>
            <tbody>
              {pendingByAffiliate.map(a => (
                <tr key={a.id} className="row-hover" style={{ borderBottom: "1px solid #f0f3fa" }}>
                  <td style={{ padding: "11px 0" }}><div style={{ fontSize: 13, fontWeight: 500, color: "#1e2d4a" }}>{a.name}</div><div style={{ fontSize: 11, color: "#8899bb" }}>{a.country}</div></td>
                  <td style={{ padding: "11px 0", fontSize: 12, color: "#6a7a9a" }}>{a.niche}</td>
                  <td style={{ padding: "11px 0", fontSize: 13 }}>{fmt(a.earned)}</td>
                  <td style={{ padding: "11px 0", fontSize: 13, color: "#2a7a4a" }}>{fmt(a.paid)}</td>
                  <td style={{ padding: "11px 0" }}><span style={{ fontSize: 14, fontWeight: 700, color: a.pending > 0 ? "#e53e3e" : "#2a7a4a" }}>{fmt(a.pending)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && isAdmin && (
        <div style={{ background: "#fff", border: "1px solid #c8d2f5", borderRadius: 12, padding: 24, marginBottom: 20 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#1e2d4a", marginBottom: 16 }}>Record Payment</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <select style={{ ...inp, cursor: "pointer" }} value={form.affiliate_id} onChange={e => setForm({ ...form, affiliate_id: e.target.value })}>
              <option value="">Select Affiliate *</option>
              {affiliates.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <input style={inp} type="number" placeholder="Amount (USD) *" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} />
            <input style={inp} placeholder="Bank Reference / TXN ID" value={form.reference} onChange={e => setForm({ ...form, reference: e.target.value })} />
            <input style={inp} placeholder="Notes (e.g. May settlement)" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
            <button className="btn-primary" onClick={submit} style={{ background: "#5472ff", border: "none", borderRadius: 8, padding: "10px 20px", color: "#fff", fontSize: 13, fontWeight: 600, transition: "all 0.15s" }}>Save Payment</button>
            <button onClick={() => setShowForm(false)} style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 16px", color: "#8899bb", fontSize: 13 }}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #e2e8f0" }}>
              {["Affiliate","Amount","Reference","Status","Created","Paid On","Action"].map(h => (
                <th key={h} style={{ textAlign: "left", fontSize: 10, color: "#8899bb", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", padding: "12px 14px" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {payments.length === 0 && <tr><td colSpan={7} style={{ padding: 40, textAlign: "center", color: "#8899bb", fontSize: 13 }}>No payment records yet</td></tr>}
            {[...payments].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map(p => {
              const aff = affiliates.find(a => a.id === p.affiliate_id);
              return (
                <tr key={p.id} className="row-hover" style={{ borderBottom: "1px solid #f0f3fa", transition: "background 0.1s" }}>
                  <td style={{ padding: "11px 14px", fontSize: 13, fontWeight: 500, color: "#1e2d4a" }}>{aff?.name || "—"}</td>
                  <td style={{ padding: "11px 14px", fontSize: 14, fontWeight: 700, color: "#2a7a4a" }}>{fmt(p.amount)}</td>
                  <td style={{ padding: "11px 14px", fontSize: 11, color: "#6a7a9a", fontFamily: "monospace" }}>{p.reference || "—"}</td>
                  <td style={{ padding: "11px 14px" }}><Badge status={p.status} /></td>
                  <td style={{ padding: "11px 14px", fontSize: 11, color: "#8899bb" }}>{fmtDate(p.created_at)}</td>
                  <td style={{ padding: "11px 14px", fontSize: 11, color: "#8899bb" }}>{p.paid_at ? fmtDate(p.paid_at) : "—"}</td>
                  <td style={{ padding: "11px 14px" }}>
                    {isAdmin && p.status === "pending" && (
                      <button onClick={() => onUpdate({ ...p, status: "paid", paid_at: new Date().toISOString().slice(0, 10) })}
                        style={{ display: "flex", alignItems: "center", gap: 6, background: "#e0f5e9", border: "1px solid #c0e8cf", borderRadius: 6, padding: "5px 10px", color: "#2a7a4a", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                        <Icon name="check" size={12} /> Mark Paid
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Applications View ────────────────────────────────────────────────────────
function ApplicationsView({ applications, onUpdate, onApprove, onReject, showToast }) {
  const [filter, setFilter] = useState("pending");
  const filtered = filter === "all" ? applications : applications.filter(a => a.status === filter);
  const pendingCount = applications.filter(a => a.status === "pending").length;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 22, fontWeight: 700, fontFamily: "'Sora', sans-serif", color: "#1e2d4a" }}>Affiliate Applications</div>
        <div style={{ fontSize: 13, color: "#6a7a9a", marginTop: 4 }}>{pendingCount} pending review</div>
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
        {["pending","approved","rejected","all"].map(s => (
          <button key={s} onClick={() => setFilter(s)}
            style={{ padding: "5px 14px", borderRadius: 20, fontSize: 11, fontWeight: 600, border: `1px solid ${filter === s ? "#c8d2f5" : "#e2e8f0"}`, background: filter === s ? "#eef1fb" : "transparent", color: filter === s ? "#5472ff" : "#8899bb", cursor: "pointer", textTransform: "capitalize" }}>
            {s === "all" ? `All (${applications.length})` : `${s} (${applications.filter(a => a.status === s).length})`}
          </button>
        ))}
      </div>

      <div style={{ display: "grid", gap: 12 }}>
        {filtered.length === 0 && (
          <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 40, textAlign: "center", color: "#8899bb", fontSize: 13 }}>
            No {filter} applications
          </div>
        )}
        {filtered.map(app => (
          <div key={app.id} style={{ background: "#fff", border: `1px solid ${app.status === "pending" ? "#ffd080" : "#e2e8f0"}`, borderRadius: 12, padding: "20px 24px" }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 20 }}>
              <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flex: 1 }}>
                <div style={{ width: 44, height: 44, borderRadius: 22, background: "#eef1fb", border: "1px solid #c8d2f5", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 700, color: "#5472ff", flexShrink: 0 }}>
                  {app.name?.charAt(0)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: "#1e2d4a" }}>{app.name}</span>
                    <Badge status={app.status} />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginTop: 10 }}>
                    {[["Email", app.email], ["Phone", app.phone], ["Country", app.country], ["Niche", app.niche], ["Website", app.website], ["Code", app.code], ["Applied", app.created_at ? new Date(app.created_at).toLocaleDateString("en-GB") : "—"]].map(([k,v]) => (
                      <div key={k}>
                        <div style={{ fontSize: 10, color: "#8899bb", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{k}</div>
                        <div style={{ fontSize: 12, color: "#1e2d4a", fontWeight: 500 }}>{v || "—"}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {app.status === "pending" && (
                <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                  <button onClick={() => onApprove(app)}
                    style={{ display: "flex", alignItems: "center", gap: 6, background: "#e0f5e9", border: "1px solid #c0e8cf", borderRadius: 8, padding: "9px 16px", color: "#2a7a4a", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                    <Icon name="check" size={14} /> Approve
                  </button>
                  <button onClick={() => onReject(app)}
                    style={{ display: "flex", alignItems: "center", gap: 6, background: "#ffe8e8", border: "1px solid #ffc0c0", borderRadius: 8, padding: "9px 16px", color: "#e53e3e", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                    <Icon name="x" size={14} /> Reject
                  </button>
                </div>
              )}
              {app.status === "approved" && (
                <div style={{ fontSize: 12, color: "#2a7a4a", fontWeight: 600, background: "#e0f5e9", padding: "6px 12px", borderRadius: 8, border: "1px solid #c0e8cf" }}>✓ Onboarded</div>
              )}
              {app.status === "rejected" && (
                <div style={{ fontSize: 12, color: "#e53e3e", fontWeight: 600, background: "#ffe8e8", padding: "6px 12px", borderRadius: 8, border: "1px solid #ffc0c0" }}>✗ Rejected</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
